// Helpers UI partagés entre les 3 vues.
import type { Task, TaskSection } from '../../hooks/queries/useTasks';
import { dueHeat } from '../../lib/tasks/dueHeat';
import { completedLast } from '../../lib/tasks/taskFocus';
import { extractDueTime } from '../../lib/tasks/taskReschedule';

import { formatAppDate } from '../../lib/dateFormat';
export const PRIORITY_META: Record<
  number,
  { label: string; className: string; dot: string; borderL: string }
> = {
  1: {
    label: 'P1',
    className: 'bg-cime-urgent-bg text-cime-urgent border-cime-urgent-border',
    dot: 'bg-cime-urgent',
    borderL: 'border-l-cime-urgent',
  },
  2: {
    label: 'P2',
    className: 'bg-cime-gold-bg text-cime-gold-text border-cime-gold-border',
    dot: 'bg-cime-gold',
    borderL: 'border-l-cime-gold',
  },
  3: {
    label: 'P3',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-400',
    borderL: 'border-l-amber-400',
  },
  4: {
    label: 'P4',
    className: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    dot: 'bg-neutral-300',
    borderL: 'border-l-neutral-300',
  },
};

export function getPriorityMeta(priority: number) {
  return PRIORITY_META[priority] ?? PRIORITY_META[3]!;
}

export function formatDueDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return formatAppDate(d, { year: false });
}

export function getDueDate(task: Task): string | null {
  return task.due_date ?? task.due_datetime ?? null;
}

export type UrgencyLevel = 'overdue' | 'critical' | 'warning' | 'normal' | 'none';

export interface UrgencyMeta {
  level: UrgencyLevel;
  className: string;
  label?: string;
}

const URGENCY_META: Record<UrgencyLevel, UrgencyMeta> = {
  overdue: {
    level: 'overdue',
    className: 'bg-cime-urgent text-white border-cime-urgent',
    label: 'En retard',
  },
  critical: {
    level: 'critical',
    className: 'bg-cime-urgent-bg text-cime-urgent border-cime-urgent-border',
    label: '< 3 j',
  },
  warning: {
    level: 'warning',
    className: 'bg-cime-gold-bg text-cime-gold-text border-cime-gold-border',
    label: '< 7 j',
  },
  normal: { level: 'normal', className: 'text-cime-text-muted' },
  none: { level: 'none', className: 'text-cime-text-muted' },
};

/**
 * Calcule l'urgence d'une tâche.
 *
 * V1 simplifiée (priority-based, en attendant `is_imperative` après EPIC A) :
 * - overdue : échéance dépassée et tâche non terminée
 * - critical : priority = 1 ET échéance < 3 jours
 * - warning  : priority = 1 ET échéance < 7 jours
 * - normal   : tâche avec échéance dans plus de 7 jours ou priorité > 1
 * - none     : pas d'échéance
 */
export function getUrgencyMeta(task: Task, now: Date = new Date()): UrgencyMeta {
  if (task.is_completed) return URGENCY_META.normal;

  const dueIso = getDueDate(task);
  if (!dueIso) return URGENCY_META.none;

  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return URGENCY_META.none;

  const msPerDay = 86_400_000;
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
  const daysUntil = Math.round((dueDay - startToday) / msPerDay);

  if (daysUntil < 0) return URGENCY_META.overdue;
  if (task.priority === 1 && daysUntil < 3) return URGENCY_META.critical;
  if (task.priority === 1 && daysUntil < 7) return URGENCY_META.warning;
  return URGENCY_META.normal;
}

export interface DueBadgeDisplay {
  label: string;
  className: string;
  /** true → pastille bordée ; false → texte nu muted. */
  bordered: boolean;
  title: string;
  style?: { backgroundColor: string; color: string; borderColor: string };
}

/**
 * Pastille d'échéance : date courte toujours en libellé, chaleur selon
 * les jours restants (graduation d'échéance). Relatif J± dans le title.
 */
export function getDueBadge(task: Task, now: Date = new Date()): DueBadgeDisplay | null {
  const dueIso = getDueDate(task);
  if (!dueIso) return null;
  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return null;

  const timeLabel = extractDueTime(task.due_datetime);
  const fullDate = formatAppDate(due) + (timeLabel ? ` à ${timeLabel}` : '');
  const label = formatDueDate(dueIso);

  if (task.is_completed) {
    return {
      label,
      className: 'text-cime-text-muted',
      bordered: false,
      title: fullDate,
    };
  }

  const msPerDay = 86_400_000;
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
  const daysUntil = Math.round((dueDay - startToday) / msPerDay);
  const heat = dueHeat({ daysUntil, isCompleted: false });
  if (!heat || heat.kind === 'done') return null;

  const relative =
    heat.kind === 'overdue'
      ? `J+${Math.abs(daysUntil)}`
      : daysUntil === 0
        ? "Aujourd'hui"
        : `J-${daysUntil}`;

  return {
    label,
    className: '',
    bordered: heat.bordered,
    title: `${relative} — ${fullDate}`,
    style: {
      backgroundColor: heat.background,
      color: heat.color,
      borderColor: heat.border,
    },
  };
}

/** Groupe les tâches par section_id (clé string ou '__none' si pas de section). */
export function groupTasksBySection(
  tasks: Task[],
): { sectionId: string | null; tasks: Task[] }[] {
  const map = new Map<string | null, Task[]>();
  for (const t of tasks) {
    if (t.parent_id) continue; // sous-tâches non gérées en V1 vues
    const key = t.section_id ?? null;
    const arr = map.get(key) ?? [];
    arr.push(t);
    map.set(key, arr);
  }
  return Array.from(map.entries()).map(([sectionId, tasks]) => ({ sectionId, tasks }));
}

/** Ordonne sections + ajoute un groupe "Sans section" en tête si présent. */
export function buildSectionGroups(
  tasks: Task[],
  sections: TaskSection[],
): { section: TaskSection | null; tasks: Task[] }[] {
  // Terminées en fin de chaque groupe (visibles seulement quand le toggle
  // « Afficher les terminées » est actif — sinon la requête les exclut déjà).
  const grouped = groupTasksBySection(tasks).map((g) => ({
    ...g,
    tasks: completedLast(g.tasks),
  }));
  const sectionsById = new Map(sections.map((s) => [s.id, s]));
  const result: { section: TaskSection | null; tasks: Task[] }[] = [];

  // Sans section d'abord
  const orphans = grouped.find((g) => g.sectionId === null);
  if (orphans && orphans.tasks.length > 0) {
    result.push({ section: null, tasks: orphans.tasks });
  }

  // Puis chaque section dans l'ordre
  for (const s of sections) {
    const group = grouped.find((g) => g.sectionId === s.id);
    result.push({ section: s, tasks: group?.tasks ?? [] });
  }

  // Sections orphelines (id en task mais section non trouvée — edge case)
  for (const g of grouped) {
    if (g.sectionId && !sectionsById.has(g.sectionId)) {
      result.push({ section: null, tasks: g.tasks });
    }
  }

  return result;
}
